import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    SortingState,
} from '@tanstack/react-table';
import { useState, useMemo, useContext, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchCampaign, fetchSupporters, exportSupporters } from '~/services/campaign';
import { toast } from 'react-hot-toast';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import TitlePanel from '~/components/CampaignDetail/TitlePanel';
import { IDonors } from '~/types/campaigns';
import { MdOutlineEdit, MdCheck, MdLock, MdMessage, MdClose } from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import { RiDeleteBinLine } from 'react-icons/ri';
import moment from 'moment';
import { BiDownload } from 'react-icons/bi';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency } from '~/common/form/Inputs';
import { Pagination } from '~/components/Pagination';
import { CampaignContext, CampaignState } from '~/context/CampaignProvider';
import Loader from '~/components/Loader';
import { buildStripePaymentUrl } from '~/common/stripeUrl';
import { formatPaymentSource } from '~/common/paymentSource';
import DonationDetailDrawer, {
    DonationDetail,
} from '~/components/Contributions/drawers/DonationDetailDrawer';
import RefundedDonationsTable from '~/components/CampaignDetail/RefundedDonationsTable';
import RefundBadge from '~/components/Contributions/RefundBadge';

type DonorRow = IDonors & { action?: string; raw: DonationDetail };

const columnHelper = createColumnHelper<DonorRow>();

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange = {
    start_date: '',
    end_date: '',
};

const PAGE_SIZE = 20;

const UniqueDonors: React.FC = () => {
    const [page, setPage] = useState<number>(1);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
    const [donationType, setDonationType] = useState<string>('');
    const [paymentMethod, setPaymentMethod] = useState<string>('');
    // 'donations' is the contribution list; 'refunded' swaps the table for this
    // campaign's refunds, which otherwise can't be reviewed here at all — a
    // full refund deletes the donation row (HHLTB-2601).
    const [view, setView] = useState<'donations' | 'refunded'>('donations');
    const [searchInput, setSearchInput] = useState<string>('');
    const [search, setSearch] = useState<string>('');
    const [activeMessage, setActiveMessage] = useState<string | null>(null);
    const [activeDonation, setActiveDonation] = useState<DonationDetail | null>(
        null
    );
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [{ campaignData }, campaignDispatch] =
        useContext<CampaignState | null>(CampaignContext);
    const { id } = useParams();

    // Fallback: if navigated directly (no context), fetch and populate context
    useEffect(() => {
        if (campaignData?.campaign || !id) return;
        fetchCampaign(id).then((data) => {
            campaignDispatch({ type: 'set_campaign', payload: data });
        });
    }, [id, campaignData?.campaign, campaignDispatch]);

    useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const campaign = campaignData?.campaign;
    const campaignPhotoInfo = campaignData?.campaignPhotoInfo;
    const isRefundView = view === 'refunded';

    const { data: donorResults, isLoading } = useQuery({
        queryKey: ['campaign-supporters', campaign?.ntaf_id, page, dateRange, donationType, paymentMethod, search],
        queryFn: () =>
            fetchSupporters(campaign.ntaf_id, page, {
                from: dateRange.start_date,
                to: dateRange.end_date,
                donation_type: donationType || undefined,
                payment_method: paymentMethod || undefined,
                search: search || undefined,
            }),
        enabled: !!campaign?.ntaf_id && view === 'donations',
        staleTime: Infinity,
    });

    const handleExport = async () => {
        if (!campaign?.ntaf_id || isExporting) return;
        setIsExporting(true);
        try {
            const blob = await exportSupporters(campaign.ntaf_id, {
                from: dateRange.start_date,
                to: dateRange.end_date,
                donation_type: donationType || undefined,
                payment_method: paymentMethod || undefined,
                search: search || undefined,
            });
            const patient = campaignData?.patient?.person;
            const nameSlug = [patient?.first_name, patient?.last_name]
                .filter(Boolean)
                .join('-')
                .replace(/[^a-zA-Z0-9-]/g, '');
            const parts = [
                'unique-donors',
                campaign.campaign_id,
                nameSlug || null,
                moment().format('YYYY-MM-DD'),
            ].filter(Boolean);
            const filename = `${parts.join('_')}.csv`;
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error exporting supporters:', error);
            toast.error('Failed to export donors');
        } finally {
            setIsExporting(false);
        }
    };

    const pages = useMemo(() => {
        if (!donorResults?.count) return 1;
        return Math.ceil(donorResults.count / PAGE_SIZE);
    }, [donorResults?.count]);

    const supporters = useMemo<DonorRow[]>(() => {
        if (!donorResults?.results) return [];
        const patient = campaignData?.patient?.person;
        const campaignName = [patient?.first_name, patient?.last_name]
            .filter(Boolean)
            .join(' ');
        return donorResults.results.map((donor: any) => {
            const first_name = donor?.name?.split(' ')[0] || '';
            const last_name = donor?.name?.split(' ')[1] || '';
            return {
                first_name,
                last_name,
                gift_date: donor.gift_date,
                created_date: donor.created_date,
                contribution: parseFloat(donor.gift_amount) || 0,
                fees_covered: donor.covered_fees,
                email: donor.email,
                address: [
                    donor.address_1,
                    donor.city_state && donor.state
                        ? `${donor.city_state}, ${donor.state}`
                        : donor.city_state || donor.state,
                    donor.zip_code,
                ]
                    .filter(Boolean)
                    .join(', '),
                stripe_charge_id: donor.stripe_charge_id || '',
                payment_method: donor.payment_method || '',
                donor_share_amount: donor.donor_share_amount,
                donation_type: donor.donation_type || '',
                gift_message: donor.gift_message || '',
                raw: {
                    id: donor.id,
                    name: donor.name,
                    first_name,
                    last_name,
                    gift_date: donor.gift_date,
                    gift_amount: donor.gift_amount,
                    donation_type: donor.donation_type,
                    payment_method: donor.payment_method,
                    covered_fees: donor.covered_fees,
                    donor_share_amount: donor.donor_share_amount,
                    donor_is_anonymous: donor.donor_is_anonymous,
                    gift_message: donor.gift_message,
                    email: donor.email,
                    address_1: donor.address_1,
                    address_2: donor.address_2,
                    city_state: donor.city_state,
                    state: donor.state,
                    zip_code: donor.zip_code,
                    country: donor.country,
                    stripe_charge_id: donor.stripe_charge_id,
                    gift_id: donor.gift_id,
                    gift_code: donor.gift_code,
                    lookup_id: donor.lookup_id,
                    ntaf_id: donor.ntaf_id ?? campaign?.ntaf_id,
                    campaign_id:
                        donor.campaign_id ?? campaign?.campaign_id ?? null,
                    campaign_name: donor.campaign_name || campaignName,
                    created_date: donor.created_date,
                    date_sync: donor.date_sync,
                    refund_summary: donor.refund_summary,
                    event_name: donor.event_name,
                    mip_locked: donor.mip_locked,
                    mip_synced_at: donor.mip_synced_at,
                },
            };
        });
    }, [
        donorResults?.results,
        campaign?.ntaf_id,
        campaign?.campaign_id,
        campaignData?.patient?.person?.first_name,
        campaignData?.patient?.person?.last_name,
    ]);

    const columns = useMemo(
        () => [
            columnHelper.accessor('first_name', {
                header: () => 'First Name',
                cell: (props) => {
                    const isAnonymous =
                        !!props.row.original.raw?.donor_is_anonymous;
                    return (
                        <div className='flex flex-col'>
                            <span>{props.renderValue()}</span>
                            {isAnonymous && (
                                <span
                                    className='mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400'
                                    title='Donor chose to stay anonymous — name is hidden on public and client-facing views'
                                >
                                    <MdLock size={10} />
                                    Anonymous
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('last_name', {
                header: () => 'Last Name',
                cell: (props) => props.renderValue(),
            }),
            columnHelper.accessor('gift_date', {
                header: () => 'Date',
                cell: (props) => {
                    const giftDate = props.getValue();
                    const createdAt = props.row.original.created_date;
                    if (!giftDate && !createdAt) return '';
                    // Match the public supporters feed: date donations by gift_date.
                    // gift_date is a date-only value; fall back to created_date
                    // (stored UTC) only when gift_date is missing.
                    const date = giftDate
                        ? moment(giftDate)
                        : moment.utc(createdAt).local();
                    return (
                        <div>
                            <div className='whitespace-nowrap'>
                                {date.format('MM/DD/YYYY')}
                            </div>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('donation_type', {
                header: () => 'Type',
                cell: (props) => {
                    const value = props.getValue();
                    if (!value) return '';
                    if (value === 'recurring') return 'Monthly';
                    return value.charAt(0).toUpperCase() + value.slice(1);
                },
            }),
            columnHelper.accessor('contribution', {
                header: () => 'Contribution',
                cell: (props) => {
                    const isPrivate = !props.row.original.donor_share_amount;
                    return (
                        <div className='flex flex-col items-start'>
                            <span className='whitespace-nowrap'>
                                {formatCurrency(props.getValue())}
                            </span>
                            <RefundBadge
                                summary={props.row.original.raw.refund_summary}
                            />
                            {isPrivate && (
                                <span
                                    className='mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400'
                                    title='Donor opted not to share the amount — hidden on public and client-facing views'
                                >
                                    <MdLock size={10} />
                                    Hidden
                                </span>
                            )}
                        </div>
                    );
                },
                sortingFn: 'basic',
            }),
            columnHelper.accessor('payment_method', {
                header: () => 'Payment Source',
                cell: (props) => {
                    const method = props.getValue();
                    const chargeId = props.row.original.stripe_charge_id;
                    const label = formatPaymentSource(method);
                    if (!chargeId) return label;
                    return (
                        <a
                            href={buildStripePaymentUrl(chargeId) || '#'}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-bold text-primary'
                        >
                            {label}
                        </a>
                    );
                },
            }),
            columnHelper.accessor('fees_covered', {
                header: 'Fees Covered',
                cell: (info) =>
                    info.getValue() === 1 ? (
                        <MdCheck className='text-primary' size={20} />
                    ) : (
                        ''
                    ),
            }),
            // Email and address share one column — separately they each held a
            // full column's width while wrapping badly (see HHLTB-2586 review).
            columnHelper.accessor('email', {
                header: () => 'Contact',
                cell: (props) => {
                    const email = props.getValue();
                    const address = props.row.original.address;
                    if (!email && !address) return '';
                    return (
                        <div className='flex flex-col gap-0.5'>
                            {email && (
                                <a
                                    className='break-all font-bold text-primary'
                                    href={`mailto:${email}`}
                                >
                                    {email}
                                </a>
                            )}
                            {address && (
                                <span className='text-[12px]/[16px] text-[#949599]'>
                                    {address}
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('gift_message', {
                header: () => 'Message',
                enableSorting: false,
                cell: (props) => {
                    const message = props.getValue();
                    if (!message) return '';
                    return (
                        <button
                            type='button'
                            onClick={() => setActiveMessage(message)}
                            className='text-primary hover:text-opacity-80'
                            title='View donor message'
                        >
                            <MdMessage size={20} />
                        </button>
                    );
                },
            }),
            columnHelper.display({
                id: 'detail',
                header: () => 'Detail',
                cell: (props) => (
                    <button
                        type='button'
                        onClick={() => setActiveDonation(props.row.original.raw)}
                        className='text-primary hover:text-opacity-80'
                        title='View donation detail'
                    >
                        <FiEye size={20} />
                    </button>
                ),
            }),
        ],
        []
    );

    const table = useReactTable({
        data: supporters,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (!campaign) return <Loader />;

    return (
        <>
            <Breadcrumb
                pageName='Unique Donors'
                grandparentPage='Campaigns'
                grandparentPath='/client-campaigns'
                parentPage={
                    campaignData?.patient?.person?.first_name && campaignData?.patient?.person?.last_name
                        ? `${campaignData.patient.person.first_name} ${campaignData.patient.person.last_name}`
                        : 'Campaign Detail'
                }
                parentPath={`/campaigns/${campaign?.campaign_id}`}
            />

            <TitlePanel
                campaign={campaign}
                campaignPhotoInfo={campaignPhotoInfo}
            />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex items-center justify-between gap-4 px-8 pb-4'>
                    <div className='flex flex-wrap items-center gap-3'>
                        <input
                            type='search'
                            name='search'
                            placeholder='Search name or email'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                        <input
                            type='date'
                            name='start_date'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={dateRange.start_date}
                            onChange={(event) => {
                                setPage(1);
                                setDateRange({
                                    ...dateRange,
                                    start_date: event.target.value,
                                });
                            }}
                        />
                        to
                        <input
                            type='date'
                            name='end_date'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={dateRange.end_date}
                            onChange={(event) => {
                                setPage(1);
                                setDateRange({
                                    ...dateRange,
                                    end_date: event.target.value,
                                });
                            }}
                        />
                        <select
                            name='donation_type'
                            disabled={isRefundView}
                            className='rounded border border-stroke bg-white p-[6px] px-3 py-2 outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50'
                            value={donationType}
                            onChange={(event) => {
                                setPage(1);
                                setDonationType(event.target.value);
                            }}
                        >
                            <option value=''>All Types</option>
                            <option value='donation'>Donation</option>
                            <option value='event'>Event</option>
                        </select>
                        <select
                            name='payment_method'
                            disabled={isRefundView}
                            className='rounded border border-stroke bg-white p-[6px] px-3 py-2 outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50'
                            value={paymentMethod}
                            onChange={(event) => {
                                setPage(1);
                                setPaymentMethod(event.target.value);
                            }}
                        >
                            <option value=''>All Sources</option>
                            <option value='credit_card'>Credit Card</option>
                            <option value='ach'>ACH</option>
                            <option value='apple_pay'>Apple Pay</option>
                            <option value='google_pay'>Google Pay</option>
                        </select>
                        <select
                            name='view'
                            className='rounded border border-stroke bg-white p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={view}
                            onChange={(event) => {
                                setPage(1);
                                setView(
                                    event.target.value as
                                        | 'donations'
                                        | 'refunded'
                                );
                            }}
                        >
                            <option value='donations'>Donations</option>
                            <option value='refunded'>Refunded</option>
                        </select>
                    </div>

                    <div className='flex items-center gap-3'>
                        <Link
                            to={`/campaigns/${campaign?.campaign_id}`}
                            className='inline-flex shrink-0 rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        >
                            Back to Console
                        </Link>
                        {/* Export covers the donations list only. */}
                        {!isRefundView && (
                            <button
                                type='button'
                                onClick={handleExport}
                                disabled={isExporting || !donorResults?.count}
                                className='inline-flex shrink-0 items-center justify-center gap-2 rounded-full border-[2px] border-black px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-current'
                            >
                                <BiDownload />
                                {isExporting ? 'Exporting...' : 'Export'}
                            </button>
                        )}
                    </div>
                </div>

                {isRefundView ? (
                    <RefundedDonationsTable
                        ntafId={campaign.ntaf_id}
                        filters={{
                            from: dateRange.start_date,
                            to: dateRange.end_date,
                            search,
                        }}
                    />
                ) : (
                    <>
                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {Math.min(PAGE_SIZE, (donorResults?.count || 0) - (page - 1) * PAGE_SIZE)} of {donorResults?.count || 0} results</p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <RiDeleteBinLine className='text-red' size={18} />
                            Delete
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map(
                                        (header) => (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                    // Contribution carries the
                                                    // refund badge underneath,
                                                    // so it needs more than the
                                                    // amount's own width.
                                                    width:
                                                        header.column.id ===
                                                        'contribution'
                                                            ? '150px'
                                                            : '',
                                                }}
                                                key={header.id}
                                            >
                                                <div
                                                    className='flex cursor-pointer select-none items-center gap-1'
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column
                                                                  .columnDef
                                                                  .header,
                                                              header.getContext()
                                                          )}
                                                    {{
                                                        asc: ' ▲',
                                                        desc: ' ▼',
                                                    }[header.column.getIsSorted() as string] ?? ''}
                                                </div>
                                            </th>
                                        )
                                    )}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => {
                                      return (
                                          <tr
                                              className='border-t border-stroke'
                                              key={row.id}
                                          >
                                              {row
                                                  .getVisibleCells()
                                                  .map((cell) => (
                                                      <td
                                                          style={{
                                                              paddingTop:
                                                                  '16px',
                                                              paddingBottom:
                                                                  '16px',
                                                          }}
                                                          key={cell.id}
                                                      >
                                                          {flexRender(
                                                              cell.column
                                                                  .columnDef
                                                                  .cell,
                                                              cell.getContext()
                                                          )}
                                                      </td>
                                                  ))}
                                          </tr>
                                      );
                                  })}
                        </tbody>
                    </table>
                </div>

                <Pagination page={page} setPage={setPage} pages={pages} totalCount={donorResults?.count} />
                    </>
                )}
            </section>

            <DonationDetailDrawer
                isOpen={activeDonation !== null}
                setIsOpen={(open) => {
                    if (!open) setActiveDonation(null);
                }}
                donation={activeDonation}
            />

            {activeMessage !== null && (
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'
                    onClick={() => setActiveMessage(null)}
                >
                    <div
                        className='max-h-[80vh] w-full max-w-lg overflow-hidden rounded-sm bg-white shadow-lg dark:bg-boxdark'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className='flex items-center justify-between border-b border-stroke px-6 py-4 dark:border-strokedark'>
                            <h3 className='text-lg font-semibold text-black dark:text-white'>
                                Donor Message
                            </h3>
                            <button
                                type='button'
                                onClick={() => setActiveMessage(null)}
                                className='rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-meta-4 dark:hover:text-white'
                            >
                                <MdClose size={20} />
                            </button>
                        </div>
                        <div className='max-h-[calc(80vh-64px)] overflow-auto whitespace-pre-wrap px-6 py-4 text-sm text-black dark:text-white'>
                            {activeMessage}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default UniqueDonors;
